import { useState, useCallback } from 'react';
import { PenIcon, UsersIconFilled, XCloseIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/base-components';
import { useDeleteRecipientMutation } from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import { EventShare } from '@rsos/reduxDataStore/src/features/eventShare/types';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import { notifyError, notifySuccess } from '@rsos/utils/toastUtils';
import { getRecipientsTags, getRecipientIcon } from '../../../utils';
import {
  TagWrapper,
  Tag,
  EditButton,
} from '../EventShareAccordionContent.styles';
import AddRecipients from './AddRecipients';

const Recipients = ({
  share,
  isActive,
}: {
  share: EventShare;
  isActive: boolean;
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const [deleteRecipient] = useDeleteRecipientMutation();

  const handleEditRecipients = useCallback(() => {
    setIsEditing(!isEditing);
  }, [isEditing]);

  const handleDeleteRecipient = useCallback(
    async (shareKey: string) => {
      try {
        await deleteRecipient({
          org: share.organization,
          sessionId: share.session_id,
          shareKey,
          uri: share.uri,
        }).unwrap();
        notifySuccess('Recipient removed successfully');
      } catch (error: unknown) {
        const errorMessage = extractErrorMessage(
          error,
          'Failed to delete recipient',
        );
        notifyError(errorMessage);
      }
    },
    [share.organization, share.session_id, share.uri, deleteRecipient],
  );

  const recipientsList = getRecipientsTags(share);

  return (
    <Box>
      <Box horizontal flow={6} align="center" color="primaryText">
        <Box horizontal flow={4} align="center">
          <UsersIconFilled height={12} width={12} />
          <Text fontWeight="bold" size="normal">
            Shared With
          </Text>
        </Box>
        {isActive && (
          <EditButton isEditing={isEditing} onClick={handleEditRecipients}>
            <Box horizontal flow={6} align="center" py="3px" px="5px">
              <PenIcon height={10} width={10} />
              <Text fontWeight="regular" size="small">
                Edit recipients
              </Text>
            </Box>
          </EditButton>
        )}
      </Box>

      <TagWrapper>
        {recipientsList.map((recipient, index) => (
          <Tag key={index}>
            <Box horizontal flow={4} align="center">
              {getRecipientIcon(recipient.type)}
              <Text size="small">{recipient.entity}</Text>
            </Box>
            {isActive && isEditing && recipient.shareKey && (
              <Box
                cursor="pointer"
                onClick={() => handleDeleteRecipient(recipient.shareKey!)}
              >
                <XCloseIcon height={8} width={8} />
              </Box>
            )}
          </Tag>
        ))}
      </TagWrapper>

      {isActive && isEditing && (
        <AddRecipients
          share={share}
          setIsEditing={setIsEditing}
          existingRecipients={recipientsList}
        />
      )}
    </Box>
  );
};

export default Recipients;
